<template>
  <section
    class="modal-steps"
    :class="siteVariant"
  >
    <ul class="steps">
      <li
        v-for="(step, ind) in variables[siteVariant].steps"
        :key="`step-${ind}`"
      >
        <span class="step-title">Step {{ ind + 1 }}</span>
        <span class="step-text">{{ step.text }}</span>
        <ol class="substeps">
          <li
            v-for="(substep, ind) in step.substeps"
            :key="`substep-${ind}`"
          >
            <span v-html="substep.text" class="substep" />
            <ul v-if="substep.extraInfo" class="extra">
              <li
                v-for="(info, ind) in substep.extraInfo"
                :key="`extra-${ind}`"
              >
                <span class="info-title">{{ info.title }}</span>
                <ul class="info-list">
                  <li
                    v-for="(item, ind) in info.items"
                    :key="`infoItem-${ind}`"
                  >
                    {{ item }}
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ol>
        <div v-if="step.bottomText" class="bottom-text">
          <div v-html="step.bottomText" />
        </div>
      </li>
    </ul>
    <p class="all-fields">All fields are required</p>
    <div class="form-container">
      <form class="submission-form" @submit.prevent="submitForm">
        <div class="double-field">
          <TheInput
            name="firstname"
            label="FIRST NAME*"
            :maxLength="50"
            placeholder="First Name"
            :dirty="v$.firstName.$dirty"
            :errorMessage="v$.firstName.$errors[0]?.$message"
            v-model:value="state.firstName"
          />
          <TheInput
            name="lastname"
            label="LAST NAME*"
            :maxLength="100"
            placeholder="Last name"
            :dirty="v$.lastName.$dirty"
            :errorMessage="v$.lastName.$errors[0]?.$message"
            v-model:value="state.lastName"
          />
        </div>
        <div class="double-field">
          <TheInput
            name="phone"
            label="MOBILE*"
            placeholder="(000) 000-0000"
            mask="(000) 000-0000"
            maxLength="14"
            :dirty="v$.phone.$dirty"
            :errorMessage="v$.phone.$errors[0]?.$message"
            v-model:value="state.phone"
          />
          <TheInput
            name="email"
            label="EMAIL*"
            placeholder="email@address.com"
            :dirty="v$.email.$dirty"
            :errorMessage="v$.email.$errors[0]?.$message"
            v-model:value="state.email"
          />
        </div>
        <TheInput
          name="dptsid"
          label="DPTS ID*"
          placeholder="Enter Your DPTS ID"
          :dirty="v$.dptsid.$dirty"
          :maxLength="7"
          :errorMessage="v$.dptsid.$errors[0]?.$message"
          v-model:value="state.dptsid"
        />
        <TheSelect
          v-model="state.zoneNumber"
          required
          label="ZONE NUMBER"
          inlineOption="Select Zone"
          :disabled="validatingDPTSID"
          :options="zoneNumberOptions"
          :dirty="v$.zoneNumber.$dirty"
          :error-message="v$.zoneNumber.$errors[0]?.$message"
        />
        <TheSelect
          v-model="state.districtLetter"
          required
          label="DISTRICT LETTER"
          inlineOption="Select District"
          :disabled="validatingDPTSID"
          :options="districtLetterOptions"
          :dirty="v$.districtLetter.$dirty"
          :error-message="v$.districtLetter.$errors[0]?.$message"
        />
        <CustomSelect
          v-model="state.dealershipName"
          id="dealership"
          required
          label="DEALERSHIP NAME*"
          placeholder="Select Dealership"
          :disabled="validatingDPTSID"
          :options="dealershipOptions"
          :dirty="v$.dealershipName.$dirty"
          :error-message="v$.dealershipName.$errors[0]?.$message"
          @selectOption="(value) => { state.dealershipName = value }"
        />
        <div class="full-row">
          <p class="youtube">Youtube Video Link*</p>
          <TheInput
            name="youtubeLink"
            label="Enter the Unlisted or Public video link to your video here:"
            placeholder="Enter Your Video Link"
            :maxLength="200"
            :dirty="v$.youtubeLink.$dirty"
            :errorMessage="v$.youtubeLink.$errors[0]?.$message"
            v-model:value="state.youtubeLink"
            class="youtube-input"
          />
        </div>
        <p class="full-paragraph">
          All information provided by the participant will be used by AHM and its agency for
          communication and video evaluation purposes regarding this contest and will not be
          shared with any outside third parties. Videos submitted to the competition as described
          in the rules become the sole and exclusive property of American Honda Motor Co., Inc.
          (including any and all right, title, and interest to copyrights in such a video) and may
          be used (including your name, image, and likeness) for any purpose, including training
          or promotion/advertising, in perpetuity, without any consideration or payment to you.
        </p>
        <div>
          <TheCheckbox
            v-model="state.termsAndConditions"
            id="submission-optin"
            text="I AGREE TO THE CONTEST TERMS AND CONDITIONS* "
            :dirty="v$.termsAndConditions.$dirty"
            :error-message="v$.termsAndConditions.$errors[0]?.$message"
          />
          <a
            class="terms-conditions-link"
            :href="termsConditions.href"
            :download="termsConditions.download"
          >
            Read full terms and conditions.
          </a>
        </div>
        <button
          class="solid-btn"
          type="submit"
          :disabled="isSubmitting"
        >
          <template v-if="!isSubmitting"> Submit </template>
          <template v-else> Loading </template>
        </button>
      </form>
    </div>
  </section>
</template>

<style scoped lang="scss">
.modal-steps {
  text-align: left;

  .steps {
    display: flex;
    flex-direction: column;
    gap: 40px;

    @media (min-width: 1281px) {
      gap: 100px;
    }
  }

  .step-title {
    font-size: clamp(28px, 62px, 3vw);
    color: $AC_Orange;
    text-transform: uppercase;
    margin-bottom: 10px;
    display: block;
  }

  .step-text {
    font-size: clamp(20px, 36px, 2.25vw);
    font-weight: 500;
  }

  .substeps {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-left: 24px;
    list-style: upper-alpha;

    @media (min-width: 1025px) {
      padding-left: 3.5%;
    }

    ::marker {
      font-size: clamp(18px, 32px, 2vw);
      font-weight: 700;
      color: $AC_Orange;
    }
  }

  .substep {
    font-size: clamp(14px, 24px, 1.5vw);
    font-weight: 500;
  }

  .extra {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-left: -24px;

    @media (min-width: 1025px) {
      margin-left: 0;
    }
  }

  .info-title {
    font-size: clamp(18px, 24px, 1.5vw);
    font-weight: 700;
    text-transform: uppercase;
  }

  .info-list {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 25px;
    list-style: disc;
    font-size: clamp(14px, 24px, 1.5vw);

    ::marker {
      font-size: clamp(14px, 24px, 1.5vw);
      font-weight: 700;
      color: $AC_Black;
    }

    @media (min-width: 1025px) {
      padding-left: 40px;
    }
  }

  :deep(.bottom-text) {
    margin-top: 20px;

    @media (min-width: 1025px) {
      margin-top: 40px;
    }

    sub {
      font-size: clamp(12px, 20px, 1.25vw);
      font-style: italic;
    }

    .concierge {
      display: inline-block;
      font-size: clamp(17px, 22px, 1.75vw);
      font-weight: 700;
      margin-bottom: 10px;
    }

    .hours,
    .tel {
      display: inline-block;
      font-size: clamp(16px, 22px, 1.75vw);
      margin-bottom: 20px;
    }

    .mail {
      display: inline-block;
      font-size: clamp(16px, 22px, 1.75vw);
      font-weight: 700;
      text-decoration: underline;
    }
  }

  :deep(.solid-btn) {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    text-decoration: none;
    color: $AC_White;
    background-color: $AC_Black;
    border: none;
    border-radius: 10px;
    width: 100%;
    max-width: 740px;
    height: 54px;
    padding: 0 16px;
    margin: 20px auto 0;
    transition: all 0.2s ease-in-out;
    cursor: pointer;

    @media (min-width: 1025px) {
      margin-top: 50px;
      font-size: 20px;
    }

    @media (min-width: 1281px) {
      height: 80px;
      font-size: 31px;
    }

    @media (min-width: 1441px) {
      margin-top: 100px;
    }

    &:not(:disabled):hover,
    &:not(:disabled):focus {
      box-shadow: 0 3px 20px rgba(226, 86, 38, 0.75);
    }

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }
  }

  :deep(.text-w-btn) {
    line-height: 2;

    .share.btnimg {
      background-image: url('@/assets/img/share-button.svg');
    }

    .copy.btnimg {
      background-image: url('@/assets/img/copy-button.svg');
    }

    .btnimg {
      width: clamp(76px, 102px, 7.4vw);
      height: clamp(25px, 33px, 2.4vw);
      display: inline-block;
      margin-bottom: -6px;
      background-size: contain;
      background-repeat: no-repeat;
    }
  }

  .all-fields {
    font-size: clamp(14px, 24px, 1.75vw);
  }

  .form-container {
    max-width: 1324px;
    margin: 0 auto;
  }

  .double-field {
    display: flex;
    flex-direction: column;
    gap: 23px;

    @media (min-width: 1025px) {
      flex-direction: row;
      gap: 14px;
    }

    .input-field {
      flex: 1;
    }
  }

  .submission-form {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 23px;

    @media (min-width: 1281px) {
      gap: 45px;
      margin-top: 60px;
    }
  }

  .youtube {
    font-size: clamp(15px, 20px, 1.5vw);
    text-transform: uppercase;
  }

  .full-paragraph {
    font-size: clamp(14px, 20px, 1.5vw);
    font-style: italic;
    margin-top: 20px;

    @media (min-width: 1281px) {
      margin-top: 0;
    }
  }

  .terms-conditions-link {
    display: inline-block;
    padding-left: 30px;
    font-size: clamp(15px, 20px, 1.5vw);
    margin-top: 5px;
    text-decoration: underline;
    color: #347ab3;
  }
}

// Acura specific styles
.acura {
  .step-title {
    font-family: 'Avenir Heavy';
  }

  :deep(.bottom-text) {
    sub {
      font-family: 'Avenir Book';
    }
  }

  :deep(.solid-btn) {
    font-family: 'Avenir Heavy';
  }

  .youtube {
    font-family: 'Avenir Heavy';
  }

  .full-row {
    font-family: 'Avenir Book';
  }

  .terms-conditions-link {
    font-family: 'Avenir Book';
  }
}

// Honda specific styles
.honda {
  .step-title {
    font-weight: 700;
  }

  :deep(.solid-btn) {
    font-weight: 700;
  }

  .youtube {
    font-weight: 700;
  }
}
</style>

<script setup>
import TheInput from '@/components/TheInput.vue';
import TheCheckbox from '@/components/TheCheckbox.vue';
import TheSelect from '@/components/TheSelect.vue';
import CustomSelect from '@/components/CustomSelect.vue';
import api from '@/api';
import {
  computed,
  onMounted,
  ref,
  watch,
  defineEmits,
} from 'vue';
import useValidate from '@vuelidate/core';
import {
  helpers,
  sameAs,
} from '@vuelidate/validators';
import { useToast } from 'vue-toastification';
import { unmaskPhoneNumber } from '@/utils/format';
import {
  scrollToFirstError,
  requiredValidator,
  DPTSIDValidator,
  phoneValidator,
  emailValidator,
  youtubeValidator,
} from '@/utils/form';

const props = defineProps({
  siteVariant: String,
});

const toast = useToast();

const zoneNumberOptions = ref([]);
const districtLetterOptions = ref([]);
const dealershipOptions = ref([]);

const variables = {
  acura: {
    steps: [
      {
        text: 'Before you submit, make sure your video follows the rules:',
        substeps: [
          { text: 'Choose an Acura ADX A-Spec Advance in your dealership. If unavailable, use another ADX or an RDX.' },
          { text: 'Do <strong>NOT</strong> record a full vehicle presentation.' },
          {
            text: 'During your presentation create a story connecting the <strong>CLIENT’S PROFILE</strong> to two of the three focus features.',
            extraInfo: [
              {
                title: 'Focus Features',
                items: [
                  'Sport-tuned fully independent suspension',
                  'Google built-in',
                  'Bang & Olufsen premium sound system',
                ],
              },
              {
                title: 'Client Profile',
                items: [
                  'Cheryl, 45, owner of an entertainment company',
                  'Married to Mark, co-owner',
                  'Cheryl drives an Audi Q3',
                  'Both enjoy the finer things in life',
                  'They are foodies and like to find new restaurants',
                ],
              },
            ],
          },
          { text: 'Your video must be 2-3 minutes in length.*' },
          { text: 'At the beginning of your video, state your name and the name of your dealership.' },
          { text: '<strong>No editing, special effects or music is permitted.</strong> Video stopping and starting or fade transitions are acceptable.*' },
          { text: 'Only one video entry per contestant.' },
        ],
        bottomText: `
          <sub>
            *Judges reserve the right to disqualify videos over or under the time requirement
            or those that do not otherwise follow the guidelines.
          </sub>
          <a href="documents/Acura_Storytelling_Competition_Rules.pdf" download="Acura Storytelling Competiton Rules" class="solid-btn">
            Download Official Rules Pdf
          </a>
        `,
      },
      {
        text: 'Upload your video to YouTube',
        substeps: [
          { text: 'Record and save your video using your smartphone.' },
          { text: 'Upload your video to a personal or dealership YouTube account. Under visibility, select Unlisted (not private).' },
          { text: '<span class="text-w-btn">Copy the provided video link by selecting the <span class="share btnimg" role="img" aria-label="share"></span> button and then selecting the <span class="copy btnimg" role="img" aria-label="copy"></span> button.</span></span>' },
          { text: 'Complete the submission form in the next step. There will be a spot for you to paste the YouTube video link in the form.' },
        ],
        bottomText: `
          <span class="concierge">
            <strong>
              For help, please contact the contest Concierge:
            </strong>
          </span><br>
          <span class="hours">
            9:00 am – 5:00 pm EDT, Monday – Friday
          </span><br>
          <a class="tel" href="tel:+18556538061">(855) 653-8061</a><br>
          <a class="mail" href="mailto:info@StorytellingComp.com">info@StorytellingComp.com</a>
        `,
      },
      {
        text: 'Submit Your Video',
      },
    ],
  },
  honda: {
    steps: [
      {
        text: 'Before you submit, make sure your video follows the rules:',
        substeps: [
          { text: 'Choose a Honda Passport TrailSport Elite in your dealership. If unavailable, use another Passport or a Pilot.' },
          { text: 'Do <strong>NOT</strong> record a full vehicle presentation.' },
          {
            text: 'During your presentation create a story connecting the <strong>CUSTOMER’S PROFILE</strong> to two of the three focus features.',
            extraInfo: [
              {
                title: 'Focus Features',
                items: [
                  'Rugged styling',
                  'Off-road tuned suspension',
                  'Ventilated seats',
                ],
              },
              {
                title: 'Customer Profile',
                items: [
                  'Mark, 54, owner of an entertainment company',
                  'Married to Cheryl, co-owner',
                  'Both enjoy the finer things in life',
                  'They have two dogs',
                  'They both like to get away to the mountains',
                  'Mark is an avid fisherman',
                ],
              },
            ],
          },
          { text: 'Your video must be 2-3 minutes in length.*' },
          { text: 'At the beginning of your video, state your name and the name of your dealership.' },
          { text: '<strong>No editing, special effects or music is permitted.</strong> Video stopping and starting or fade transitions are acceptable.*' },
          { text: 'Only one video entry per contestant.' },
        ],
        bottomText: `
          <sub>
            *Judges reserve the right to disqualify videos over or under the time requirement
            or those that do not otherwise follow the guidelines.
          </sub>
          <a href="documents/Honda_Storytelling_Competition_Rules.pdf" download="Honda Storytelling Competiton Rules" class="solid-btn">
            Download Official Rules Pdf
          </a>
        `,
      },
      {
        text: 'Upload your video to YouTube',
        substeps: [
          { text: 'Record and save your video using your smartphone.' },
          { text: 'Upload your video to a personal or dealership YouTube account. Under visibility, select Unlisted (not private).' },
          { text: '<span class="text-w-btn">Copy the provided video link by selecting the <span class="share btnimg" role="img" aria-label="share"></span> button and then selecting the <span class="copy btnimg" role="img" aria-label="copy"></span> button.</span></span>' },
          { text: 'Complete the submission form in the next step. There will be a spot for you to paste the YouTube video link in the form.' },
        ],
        bottomText: `
          <span class="concierge">
            <strong>
              For help, please contact the contest Concierge:
            </strong>
          </span><br>
          <span class="hours">
            9:00 am – 5:00 pm EDT, Monday – Friday
          </span><br>
          <a class="tel" href="tel:+18556538061">(855) 653-8061</a><br>
          <a class="mail" href="mailto:info@StorytellingComp.com">info@StorytellingComp.com</a>
        `,
      },
      {
        text: 'Submit Your Video',
      },
    ],
  },
};

const emit = defineEmits(['closeModal']);

const state = ref({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  dptsid: '',
  zoneNumber: '',
  districtLetter: '',
  dealershipName: '',
  youtubeLink: '',
  termsAndConditions: false,
});

const termsConditions = computed(() => (
  props.siteVariant === 'acura' ? ({
    href: 'documents/Acura_Storytelling_Competition_Rules.pdf',
    download: 'Acura Storytelling Competiton Rules',
  }) : ({
    href: 'documents/Honda_Storytelling_Competition_Rules.pdf',
    download: 'Honda Storytelling Competiton Rules',
  })
));

const rules = computed(() => ({
  firstName: { requiredValidator },
  lastName: { requiredValidator },
  phone: { requiredValidator, phoneValidator },
  email: { requiredValidator, emailValidator },
  dptsid: {
    requiredValidator,
    DPTSIDValidator,
  },
  zoneNumber: { requiredValidator },
  districtLetter: { requiredValidator },
  dealershipName: { requiredValidator },
  youtubeLink: { requiredValidator, youtubeValidator },
  termsAndConditions: {
    sameAs: helpers.withMessage('Please agree to the terms and conditions.', sameAs(true)),
  },
}));

const v$ = useValidate(rules, state.value, { $autoDirty: true });

const fetchZoneCodes = async () => {
  try {
    const { data } = await api.data.getZoneCodes(props.siteVariant);
    zoneNumberOptions.value = data;
  } catch (e) {
    toast.error('An error ocurred while fetching Zone Codes, please try again.');
  }
};

const fetchDistrictLetters = async () => {
  try {
    const { data } = await api.data.getDistrictLetters(props.siteVariant);
    districtLetterOptions.value = data;
  } catch (e) {
    toast.error('An error ocurred while fetching District Letters, please try again.');
  }
};

const fetchDealerships = async () => {
  try {
    const { data } = await api.data.getDealerships(props.siteVariant);
    dealershipOptions.value = data;
  } catch (e) {
    toast.error('An error ocurred while fetching Dealerships, please try again.');
  }
};

const validatingDPTSID = ref(false);

const validateDPTSID = async (dptsid) => {
  validatingDPTSID.value = true;
  try {
    const { data, status } = await api.data.validateDPTSId(dptsid, props.siteVariant);
    if (status === 204) {
      return;
    }
    const { dealerName, zoneCode, districtCode } = data;

    state.value.dealershipName = dealerName;
    state.value.zoneNumber = zoneCode;
    state.value.districtLetter = districtCode;
  } catch (e) {
    toast.error('An error ocurred while validating DPTS ID, please try again.');
  } finally {
    validatingDPTSID.value = false;
  }
};

const isSubmitting = ref(false);

const postSubmission = async (data) => {
  try {
    await api.registrationForm.postSubmit(data, props.siteVariant);
    toast.success(
      'Thank you for your submission! Watch your email for an announcement of the zone winners and national finalists May 12th.',
      { timeout: 20000 },
    );
    emit('closeModal');
  } catch (e) {
    toast.error(e.response.data.message || 'An error ocurred while submitting, please try again.', {
      timeout: 20000,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const submitForm = async () => {
  isSubmitting.value = true;
  await v$.value.$validate();
  state.value.phone = unmaskPhoneNumber(state.value.phone);
  if (!v$.value.$error) {
    postSubmission(state.value);
  } else {
    isSubmitting.value = false;
    scrollToFirstError(document);
    const toastMessage = v$.value.$errors[0].$validator === 'requiredValidator'
      ? 'Please provide values for all required fields.'
      : v$.value.$errors[0].$message;
    toast.error(toastMessage);
  }
};

watch(
  () => state.value.dptsid,
  () => {
    if (state.value.dptsid.length === 7) {
      validateDPTSID(state.value.dptsid);
    }
  },
);

onMounted(() => {
  fetchZoneCodes();
  fetchDistrictLetters();
  fetchDealerships();
});
</script>
